/* Sacramento Creative — Midnight Bold theme JS (vanilla, no dependencies) */

const Sacramento = {
  init() {
    this.header.init();
    this.cart.init();
    this.countdown.init();
    this.productGallery.init();
    this.productForm.init();
    this.quickAdd.init();
    this.animations.init();
    this.countUp.init();
    this.sorting.init();
    this.volumeDefault.init();
  },

  /* Sticky header: transparent over hero, solid after scrolling past it */
  header: {
    init() {
      const header = document.querySelector('.site-header');
      if (!header) return;

      const sentinel = document.querySelector('.hero');
      if (sentinel) {
        const observer = new IntersectionObserver(
          ([e]) => header.classList.toggle('header--scrolled', !e.isIntersecting),
          { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
        );
        observer.observe(sentinel);
      } else {
        header.classList.add('header--scrolled');
      }

      const toggle = header.querySelector('[data-menu-toggle]');
      const menu = header.querySelector('[data-mobile-menu]');
      const close = header.querySelector('[data-menu-close]');
      if (!toggle || !menu) return;

      const setOpen = (open) => {
        menu.classList.toggle('is-open', open);
        menu.setAttribute('aria-hidden', String(!open));
        toggle.setAttribute('aria-expanded', String(open));
        document.body.style.overflow = open ? 'hidden' : '';
      };

      toggle.addEventListener('click', () => setOpen(true));
      if (close) close.addEventListener('click', () => setOpen(false));
      menu.addEventListener('click', (e) => {
        if (e.target.tagName === 'A') setOpen(false);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') setOpen(false);
      });
    }
  },

  cart: {
    init() {
      this.updateCount();
    },
    async addItem(variantId, quantity = 1, options = {}) {
      const useBundler = options.bundler === true;
      const url = useBundler ? '/cart/add.js?bundler-cart-call' : '/cart/add.js';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      const data = await res.json();
      await this.updateCount();
      return data;
    },
    async updateCount() {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    }
  },

  countdown: {
    init() {
      document.querySelectorAll('[data-countdown]').forEach((root) => {
        const target = new Date(root.dataset.countdownTarget || '2026-07-04T00:00:00-07:00');
        const els = {
          d: root.querySelector('[data-days]'),
          h: root.querySelector('[data-hours]'),
          m: root.querySelector('[data-mins]'),
          s: root.querySelector('[data-secs]')
        };
        const pad = (n) => String(n).padStart(2, '0');
        const update = () => {
          const diff = target - Date.now();
          if (diff <= 0) {
            els.d.textContent = els.h.textContent = els.m.textContent = els.s.textContent = '00';
            return;
          }
          els.d.textContent = pad(Math.floor(diff / 86400000));
          els.h.textContent = pad(Math.floor((diff % 86400000) / 3600000));
          els.m.textContent = pad(Math.floor((diff % 3600000) / 60000));
          els.s.textContent = pad(Math.floor((diff % 60000) / 1000));
        };
        update();
        setInterval(update, 1000);
      });
    }
  },

  /* PDP gallery: full slider — click main image / arrows / swipe to switch,
     thumbnails stay in sync. No lightbox, keep it fast. */
  productGallery: {
    init() {
      const gallery = document.querySelector('[data-gallery]');
      if (!gallery) return;
      const main = gallery.querySelector('[data-gallery-main]');
      const frame = gallery.querySelector('[data-gallery-frame]') || main;
      const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      const counter = gallery.querySelector('[data-gallery-count]');
      if (!main || thumbs.length < 2) return;

      let index = 0;

      const show = (i) => {
        index = (i + thumbs.length) % thumbs.length; // wrap around both ways
        const thumb = thumbs[index];
        const img = document.createElement('img');
        img.src = thumb.dataset.fullSrc;
        img.alt = thumb.querySelector('img')?.alt || '';
        frame.replaceChildren(img);
        thumbs.forEach((t, n) => t.classList.toggle('is-active', n === index));
        if (counter) counter.textContent = `${index + 1} / ${thumbs.length}`;
      };

      thumbs.forEach((thumb, n) => {
        thumb.addEventListener('click', () => show(n));
      });

      /* Click on the big image -> next slide (arrows handle themselves) */
      main.addEventListener('click', (e) => {
        if (e.target.closest('[data-gallery-prev], [data-gallery-next]')) return;
        show(index + 1);
      });

      const prev = gallery.querySelector('[data-gallery-prev]');
      const next = gallery.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => show(index - 1));
      if (next) next.addEventListener('click', () => show(index + 1));

      /* Keyboard support */
      main.setAttribute('tabindex', '0');
      main.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') show(index - 1);
        if (e.key === 'ArrowRight') show(index + 1);
      });

      /* Touch swipe for mobile */
      let touchX = 0;
      let touchY = 0;
      main.addEventListener('touchstart', (e) => {
        touchX = e.changedTouches[0].clientX;
        touchY = e.changedTouches[0].clientY;
      }, { passive: true });
      main.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - touchX;
        const dy = e.changedTouches[0].clientY - touchY;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        show(dx < 0 ? index + 1 : index - 1);
      }, { passive: true });
    }
  },

  /* PDP form: resolve variant from selected option pills, AJAX add to cart */
  productForm: {
    init() {
      const page = document.querySelector('[data-product-page]');
      if (!page) return;
      const form = page.querySelector('[data-product-form], form[action*="/cart/add"]');
      const jsonEl = page.querySelector('[data-product-json]');
      if (!form || !jsonEl) return;

      const product = JSON.parse(jsonEl.textContent);
      const idInput = form.querySelector('[data-variant-id-input]');
      const addBtn = form.querySelector('[data-add-to-cart]');
      const priceEl = page.querySelector('[data-product-price]');
      const moneyFormat = (cents) => '$' + (cents / 100).toFixed(2);

      const resolveVariant = () => {
        const selected = [];
        form.querySelectorAll('[data-option-index]').forEach((group) => {
          const checked = group.querySelector('input:checked');
          selected[Number(group.dataset.optionIndex)] = checked ? checked.value : null;
        });
        if (!selected.length) return product.variants[0];
        return product.variants.find((v) =>
          selected.every((val, i) => val === null || v.options[i] === val)
        );
      };

      /* Show the image of the selected variant (e.g. the NATURAL tee when
         the NATURAL color is picked). Prefer clicking the matching thumbnail
         so the gallery slider state stays in sync; fall back to swapping
         the main image directly when the variant image has no thumbnail. */
      const syncImage = (variant) => {
        if (!variant.featured_image || !variant.featured_image.src) return;
        const file = variant.featured_image.src
          .split('?')[0]
          .split('/')
          .pop();
        const thumb = Array.from(document.querySelectorAll('[data-gallery-thumb]')).find((t) =>
          (t.dataset.fullSrc || '').includes(file)
        );
        if (thumb) {
          if (!thumb.classList.contains('is-active')) thumb.click();
          return;
        }
        const frame = document.querySelector('[data-gallery-frame]');
        if (frame) {
          const img = document.createElement('img');
          img.src = variant.featured_image.src;
          img.alt = variant.title || '';
          frame.replaceChildren(img);
        }
      };

      const sync = () => {
        const variant = resolveVariant();
        if (!variant) {
          addBtn.disabled = true;
          addBtn.textContent = 'Unavailable';
          return;
        }
        idInput.value = variant.id;
        syncImage(variant);
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? 'Add to cart \u25B6\u25B6' : 'Sold out';
        if (priceEl) {
          let html = '<div class="price' + (variant.compare_at_price > variant.price ? ' price--on-sale' : '') + '">';
          html += '<span class="price__current">' + moneyFormat(variant.price) + '</span>';
          if (variant.compare_at_price > variant.price) {
            html += '<s class="price__compare">' + moneyFormat(variant.compare_at_price) + '</s>';
          }
          priceEl.innerHTML = html + '</div>';
        }
      };

      form.querySelectorAll('[data-option-input]').forEach((input) => {
        input.addEventListener('change', sync);
      });

      form.addEventListener('submit', async (e) => {
        if (form.querySelector('.shopify-payment-button')?.contains(e.submitter)) return;

        const hasBundler = form.querySelector('.bndlr-quantity-break, input[name="bundle_quantity"]');

        /* When Bundler quantity-break widget is present, use native form
           POST so the app can attach bundle metadata for checkout discounts. */
        if (hasBundler) {
          addBtn.disabled = true;
          addBtn.textContent = 'Adding\u2026';
          return;
        }

        e.preventDefault();
        const original = addBtn.textContent;
        addBtn.disabled = true;
        addBtn.textContent = 'Adding\u2026';
        try {
          const bundleRadio = form.querySelector('input[name="bundle_quantity"]:checked');
          const qtyInput = form.querySelector('input[name="quantity"]');
          const qty = Math.max(
            1,
            parseInt(bundleRadio && bundleRadio.value, 10) ||
              parseInt(qtyInput && qtyInput.value, 10) ||
              1
          );
          await Sacramento.cart.addItem(Number(idInput.value), qty);
          addBtn.textContent = 'Added \u2713';
        } catch (err) {
          addBtn.textContent = err.message;
        }
        setTimeout(() => {
          addBtn.textContent = original;
          addBtn.disabled = false;
        }, 2000);
      });

      sync();
    }
  },

  /* Product card quick add: AJAX, no page reload */
  quickAdd: {
    init() {
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn) return;
        e.preventDefault();
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding\u2026';
        try {
          await Sacramento.cart.addItem(Number(btn.dataset.variantId));
          btn.textContent = 'Added \u2713';
        } catch (err) {
          btn.textContent = err.message;
        }
        setTimeout(() => {
          btn.textContent = original;
          btn.disabled = false;
        }, 2000);
      });
    }
  },

  /* Fade-in on scroll */
  animations: {
    init() {
      const els = document.querySelectorAll('[data-animate]');
      if (!els.length) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        els.forEach((el) => el.classList.add('is-visible'));
        return;
      }
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add('is-visible');
              obs.unobserve(e.target);
            }
          });
        },
        { threshold: 0.1 }
      );
      els.forEach((el) => obs.observe(el));
    }
  },

  /* Hero social proof counter animates up on load */
  countUp: {
    init() {
      document.querySelectorAll('[data-countup]').forEach((el) => {
        const target = Number(el.dataset.target || 0);
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          el.textContent = target.toLocaleString('en-US');
          return;
        }
        const duration = 1400;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased).toLocaleString('en-US');
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }
  },

  /* Volume discount widget (Bundler app): preselect the 2nd option by default.
     The widget is injected by the app's script after page load, so we watch
     the DOM and click the second radio once — the app's own handlers then
     recalculate the price, discount and cart payload. */
  volumeDefault: {
    DEFAULT_INDEX: 1,
    /* The bundle option chosen by the shopper. Survives widget re-renders:
       when the app rebuilds its DOM (e.g. on color/size change) and resets
       the selection to "Buy 1", we restore this index. */
    userIndex: null,
    init() {
      if (!document.querySelector('[data-product-page]')) return;
      this.apply();
      /* The app re-renders the widget on variant change / price update,
         wiping both our decoration and the shopper's selection — keep the
         observer alive for the whole page lifetime and re-apply. */
      const obs = new MutationObserver(() => this.apply());
      obs.observe(document.body, { childList: true, subtree: true });
    },
    getRadios(container) {
      return Array.from(container.querySelectorAll('input[type="radio"]'));
    },
    apply() {
      const container = document.querySelector(
        '[class*="bundler"], [id*="bundler"], [class*="volume-discount"], [class*="quantity-break"]'
      );
      if (!container) return;
      const radios = this.getRadios(container);
      if (radios.length <= this.DEFAULT_INDEX) return;

      this.decorate(container, radios);

      const want = this.userIndex === null ? this.DEFAULT_INDEX : this.userIndex;
      const target = radios[Math.min(want, radios.length - 1)];
      if (target && !target.checked) target.click();
      if (this.userIndex === null) this.userIndex = want;
      this.syncSelected(radios);
    },
    /* Tag each option card with our own classes so the theme CSS can style
       the app-rendered widget regardless of its internal markup. */
    decorate(container, radios) {
      radios.forEach((radio, i) => {
        /* Bundler markup: <div.bndlr-radio-container> <input/> <label> ... */
        const card = radio.closest('.bndlr-radio-container') || radio.closest('label') || radio.parentElement;
        if (!card || card.classList.contains('sc-bulk-option')) return;
        card.classList.add('sc-bulk-option');
        if (i === this.DEFAULT_INDEX) {
          card.classList.add('sc-bulk-featured');
          const savings = card.querySelector('.bndlr-savings-text, .bndlr-savings-badge');
          let sourceText = '';
          if (savings) {
            sourceText = savings.textContent;
            savings.remove();
          }
          const wrapper = card.querySelector('.bndlr-text-price-wrapper') || card.querySelector('label') || card;
          const price = wrapper.querySelector('.bndlr-price');
          const star = this.buildStar(sourceText);
          if (price) {
            wrapper.insertBefore(star, price);
          } else {
            wrapper.appendChild(star);
          }
        }
      });
      if (!container.dataset.scBulkBound) {
        container.dataset.scBulkBound = '1';
        container.addEventListener('change', () => {
          const current = this.getRadios(container);
          const checkedIndex = current.findIndex((r) => r.checked);
          if (checkedIndex >= 0) this.userIndex = checkedIndex;
          this.syncSelected(current);
        });
      }
    },
    /* Red starburst badge with "SAVE n%" centered inside, placed in the
       middle of the featured card. The percent is read from the app's own
       savings badge when available. */
    buildStar(sourceText) {
      const match = (sourceText || '').match(/\d+\s*%/);
      const percent = match ? match[0].replace(/\s+/g, '') : '10%';
      const spikes = 14;
      const pts = [];
      for (let i = 0; i < spikes * 2; i++) {
        const r = i % 2 === 0 ? 50 : 37;
        const a = (Math.PI * i) / spikes - Math.PI / 2;
        pts.push((50 + r * Math.cos(a)).toFixed(1) + ',' + (50 + r * Math.sin(a)).toFixed(1));
      }
      const star = document.createElement('span');
      star.className = 'sc-bulk-star';
      star.setAttribute('aria-hidden', 'true');
      star.innerHTML =
        '<svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">' +
        '<polygon points="' + pts.join(' ') + '" fill="#C8102E"/>' +
        '</svg>' +
        '<span class="sc-bulk-star__text">SAVE<br>' + percent + '</span>';
      return star;
    },
    syncSelected(radios) {
      radios.forEach((radio) => {
        const card = radio.closest('.bndlr-radio-container') || radio.closest('label') || radio.parentElement;
        if (card) card.classList.toggle('sc-bulk-selected', radio.checked);
      });
    }
  },

  /* Collection sorting */
  sorting: {
    init() {
      const select = document.querySelector('[data-sort-select]');
      if (!select) return;
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.href = url.toString();
      });
    }
  }
};

document.addEventListener('DOMContentLoaded', () => Sacramento.init());
